import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { trackEvent, resetCartTimer } from '../utils/analytics';
import { getCustomizationPrice, getLineDailyOfferSavings } from '../utils/pricing';
import { foreignCartLines } from '../utils/branchSwitch';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  // The live cart for async paths. A store switch fired from a callback created
  // renders ago (Landing's GPS auto-detect resolves seconds after mount) used to
  // read that render's EMPTY cart, switch the store and carry nothing — leaving
  // the old store's lines in the cart, counted ₹0 by the app and charged at the
  // old store's price by the server (2026-10-14, Business Rule #50).
  const cartRef = useRef(cart);
  cartRef.current = cart;
  // One store repair at a time (Cart / Checkout can both ask while mounted).
  const repairInFlightRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [cartReady, setCartReady] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  // Bumped on EVERY add so the CartDrawer can restart its 4s auto-close window
  // each time. `setCartDrawerOpen(true)` is a no-op in React when the drawer is
  // already open, so without a changing signal a quick second add rode the
  // first add's countdown and closed almost immediately — the "floating cart
  // doesn't come again" report. The drawer keys its auto-close effect on this.
  const [cartDrawerNonce, setCartDrawerNonce] = useState(0);
  // Business Rule #23: ``allow_with_subscription`` is the per-coupon
  // opt-in for stacking with a bundle pass. Captured from the server's
  // ``/discount/validate`` response so Cart / Checkout can suppress the
  // coupon line when a pass is firing AND neither side opted in.
  // `source` distinguishes how a coupon got here so the cart UI can render
  // the right affordance: 'manual' (typed code) and 'suggested' (tapped from
  // the Available list) are user choices and stick until explicitly removed;
  // 'auto' is the silent auto-apply pick that re-evaluates as the cart
  // changes. Default '' (empty slot).
  const [appliedDiscount, setAppliedDiscount] = useState({ code: '', amount: 0, allow_with_subscription: false, source: '' });
  // Business Rule #24: coupon-trigger BOGO offer the customer activated by
  // typing a code. Persisted in context so Cart → Checkout navigation keeps
  // it (the coupon input lives on Cart; Checkout needs to send the id with
  // the order). Cleared by `clearCart` and `setAppliedDiscount({...})`.
  const [activeBogoOffer, setActiveBogoOffer] = useState(null);
  // Business Rule #37 — cart-level "remember my taste for future orders" toggle.
  // ON (default) → the backend folds each line's `taste_overrides` into the
  // customer's saved `coffee_preferences` at order creation; OFF → the taste
  // selection applies to THIS order only (the per-line override still drives the
  // note, the profile is left untouched). localStorage-backed so it survives the
  // Cart → Checkout navigation + a refresh on Checkout.
  const [rememberTaste, setRememberTasteState] = useState(() => {
    try {
      const v = localStorage.getItem('pbc_remember_taste');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const setRememberTaste = (val) => {
    setRememberTasteState(val);
    try { localStorage.setItem('pbc_remember_taste', val ? '1' : '0'); } catch { /* private mode */ }
  };
  const { user, token, loading: authLoading } = useAuth();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (authLoading) return; // wait for auth to resolve before touching cart

    if (user && token) {
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      if (localCart.length > 0) {
        // Guest had items — keep them visible and push to server
        setCart(localCart);
        setCartReady(true);
        axios.post(`${API}/cart`, localCart, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => console.error('Failed to sync cart:', err));
        localStorage.removeItem('cart');
      } else {
        fetchCart();
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCart(localCart);
      setCartReady(true);
    }
  }, [user, token, authLoading]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = response.data.items || [];
      setCart(items);
      // No open cart → no placement timer. The server empties a paid order's
      // lines even when this app never saw the payment succeed (Business Rule
      // #6 — e.g. iOS killed the app during the UPI-app switch), so drop the
      // old cart's timer here or the next order's placement_duration_seconds
      // runs from the previous cart's first add.
      if (items.length === 0) resetCartTimer();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
      setCartReady(true);
    }
  };

  const updateCart = async (newCart) => {
    cartRef.current = newCart;
    setCart(newCart);
    
    if (user && token) {
      try {
        await axios.post(`${API}/cart`, newCart, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to update cart:', error);
      }
    } else {
      localStorage.setItem('cart', JSON.stringify(newCart));
    }
  };

  // Open the cart preview AND bump the nonce so a repeat add restarts the
  // drawer's 4s auto-close window (CartDrawer keys its timer on the nonce).
  const openCartDrawer = () => {
    setCartDrawerOpen(true);
    setCartDrawerNonce((n) => n + 1);
  };

  // Merge one add into a cart array (pure): bump the matching line — same
  // item, customization, daily offer and sub-free flag, never the free coffee —
  // or append a new line.
  const mergeIntoCart = (base, menuItem, quantity = 1, customization = null, customizationPrice = 0, offerMeta = null, subMeta = null) => {
    const existingItemIndex = base.findIndex(
      item => item.menu_item_id === menuItem.id && JSON.stringify(item.customization) === JSON.stringify(customization)
        && (item.daily_offer_id || null) === (offerMeta?.daily_offer_id || null)
        && (!!item.is_sub_free) === (!!subMeta)
        && !item.is_free_coffee
    );
    if (existingItemIndex >= 0) {
      const next = [...base];
      next[existingItemIndex] = { ...next[existingItemIndex], quantity: next[existingItemIndex].quantity + quantity };
      return next;
    }
    const cartEntry = { menu_item_id: menuItem.id, quantity, customization, customization_price: customizationPrice };
    if (offerMeta) {
      cartEntry.daily_offer_id = offerMeta.daily_offer_id;
      cartEntry.offer_price = offerMeta.offer_price;
      cartEntry.offer_title = offerMeta.offer_title;
    }
    if (subMeta) {
      cartEntry.is_sub_free = true;
      cartEntry.sub_name = subMeta.sub_name || 'Subscription';
    }
    return [...base, cartEntry];
  };

  const trackCartAdd = (menuItem) => {
    // Explicitly thread branch_id so the funnel chart's "Added to Cart"
    // step stays populated when an admin filters by branch (Phase B.6).
    // menuItem.branch_id is the most reliable signal — every cart line
    // is per-branch since the menu is per-branch. Falls through to the
    // localStorage fallback in `trackEvent` when missing.
    trackEvent('cart_add', {
      branch_id: menuItem.branch_id,
      metadata: { menu_item_id: menuItem.id, name: menuItem.name },
    });
  };

  const addToCart = (menuItem, quantity = 1, customization = null, customizationPrice = 0, offerMeta = null, subMeta = null) => {
    updateCart(mergeIntoCart(cart, menuItem, quantity, customization, customizationPrice, offerMeta, subMeta));
    openCartDrawer();
    trackCartAdd(menuItem);
  };

  // Add several lines in ONE cart write — e.g. a tiffin and its paired coffee
  // (Business Rule #58). Two back-to-back addToCart calls would each start from
  // the same `cart` and the second would drop the first.
  // Each entry: {menuItem, quantity?, customization?, customizationPrice?}.
  const addItemsToCart = (entries) => {
    let next = cart;
    for (const e of entries || []) {
      if (!e?.menuItem?.id) continue;
      next = mergeIntoCart(next, e.menuItem, e.quantity || 1, e.customization || null, e.customizationPrice || 0);
    }
    if (next === cart) return;
    updateCart(next);
    openCartDrawer();
    (entries || []).forEach(e => e?.menuItem?.id && trackCartAdd(e.menuItem));
  };

  // Add a free coffee item to cart (max 1, base price = ₹0, extras charged)
  const addFreeCoffee = (menuItem, customizations = null, customizationPrice = 0) => {
    const hasFreeCoffee = cart.some(item => item.is_free_coffee);
    if (hasFreeCoffee) return false;
    const cartEntry = {
      menu_item_id: menuItem.id,
      quantity: 1,
      customization: customizations,
      customization_price: customizationPrice || 0,
      is_free_coffee: true,
    };
    const newCart = [...cart, cartEntry];
    updateCart(newCart);
    openCartDrawer();
    return true;
  };

  const hasFreeCoffeeInCart = () => cart.some(item => item.is_free_coffee);

  const customizationMatch = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const removeFromCart = (menuItemId, customization = null) => {
    // Find the first matching item index for precise removal
    const idx = cart.findIndex(
      item => item.menu_item_id === menuItemId && customizationMatch(item.customization, customization)
    );
    if (idx >= 0) {
      const newCart = [...cart];
      newCart.splice(idx, 1);
      updateCart(newCart);
    } else {
      // Fallback: remove by menu_item_id only if customization match failed
      const fallbackIdx = cart.findIndex(item => item.menu_item_id === menuItemId);
      if (fallbackIdx >= 0) {
        const newCart = [...cart];
        newCart.splice(fallbackIdx, 1);
        updateCart(newCart);
      }
    }
  };

  const removeByIndex = (index) => {
    if (index >= 0 && index < cart.length) {
      const newCart = [...cart];
      newCart.splice(index, 1);
      updateCart(newCart);
    }
  };

  const updateQuantity = (menuItemId, quantity, customization = null) => {
    if (quantity <= 0) {
      removeFromCart(menuItemId, customization);
      return;
    }

    // Free coffee claim is limited to exactly 1 unit per order.
    // If user tries to increment the free-coffee entry, cap it at 1.
    // Extra units must be added as separate regular (paid) items.
    const targetItem = cart.find(
      item => item.menu_item_id === menuItemId && customizationMatch(item.customization, customization)
    );
    if (targetItem?.is_free_coffee && quantity > 1) {
      quantity = 1;
    }

    const newCart = cart.map(item =>
      item.menu_item_id === menuItemId && customizationMatch(item.customization, customization)
        ? { ...item, quantity }
        : item
    );
    updateCart(newCart);
  };

  // Change ONE cart line's quantity by its position (0 → remove). The Cart page
  // and the cart preview use this: `updateQuantity` matches by item +
  // customization and so moved every matching line together — a daily-offer
  // line and a full-price line of the same drink went up and down as one.
  // Free coffee stays capped at 1 (Business Rule #5).
  const updateQuantityAt = (index, quantity) => {
    if (index < 0 || index >= cart.length) return;
    if (quantity <= 0) {
      removeByIndex(index);
      return;
    }
    const q = cart[index].is_free_coffee ? Math.min(quantity, 1) : quantity;
    updateCart(cart.map((item, i) => (i === index ? { ...item, quantity: q } : item)));
  };

  // Also ends the cart's placement timer — OrderConfirmation clears the cart
  // after a web Cashfree redirect but never reset the timer itself.
  const clearCart = async () => {
    resetCartTimer();
    setCart([]);
    setAppliedDiscount({ code: '', amount: 0, allow_with_subscription: false, source: '' });
    setActiveBogoOffer(null);
    localStorage.removeItem('cart');
    if (user && token) {
      try {
        await axios.delete(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to clear cart:', error);
      }
    }
  };

  // Business Rule #50 — a store switch CARRIES the cart across instead of
  // clearing it. The single switch path for every entry point (Home picker,
  // the resume "you're near X" prompt, Checkout's Change link): persists the
  // new store, then asks the server to re-resolve each line at the target by
  // parent_sku (re-priced, uncarried / sold-out / wrong-size lines dropped,
  // daily-offer + combo pricing re-validated, the free-coffee claim re-pointed)
  // and replaces the cart with the result. Coupon + BOGO are reset because
  // they are branch-scoped and the Cart re-evaluates them anyway.
  // Fail-safe: if the remap call fails, fall back to the legacy clear — a
  // customer must never reach payment holding wrong-store ids / prices
  // (Business Rule #27). Resolves to the shape `buildSwitchSummary` renders.
  const switchBranch = async (branch) => {
    if (!branch?.id) return null;
    let prevId = null;
    try { prevId = JSON.parse(localStorage.getItem('selectedBranch') || 'null')?.id || null; } catch { /* corrupt */ }
    localStorage.setItem('selectedBranch', JSON.stringify(branch));
    const changed = prevId !== branch.id;
    const current = cartRef.current;
    const hadCart = current.length > 0;
    const base = { branch, changed, hadCart, moved: [], dropped: [], offersRemoved: [], items: current, cleared: false };
    if (!changed || !hadCart) return base;
    try {
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const res = await axios.post(`${API}/cart/remap`, { items: current, target_branch_id: branch.id }, cfg);
      const items = res.data?.items || [];
      setAppliedDiscount({ code: '', amount: 0, allow_with_subscription: false, source: '' });
      setActiveBogoOffer(null);
      await updateCart(items);
      return {
        ...base,
        items,
        moved: res.data?.moved || [],
        dropped: res.data?.dropped || [],
        offersRemoved: res.data?.offers_removed || [],
      };
    } catch (error) {
      console.error('Failed to move cart to the new store, clearing it:', error);
      await clearCart();
      return { ...base, items: [], cleared: true };
    }
  };

  /**
   * Move cart lines that aren't on the current store's menu onto it (Business
   * Rule #50). Cart and Checkout call this with their branch-scoped
   * `/menu/items/batch` rows: a line those rows don't know is another store's
   * menu row (or a deleted one) — the app would count it as ₹0 while
   * `create_order` refuses it. `/cart/remap` moves only those lines (by
   * parent_sku, at this store's price), drops what this store can't serve, and
   * leaves this store's own lines exactly as they are.
   * @param {{id: string, name?: string}} branch — the current store.
   * @param {Array<{id: string}>|null} menuRows — the batch rows, or null to force.
   * @returns {Promise<{branch, moved, dropped, offersRemoved}|null>} null when nothing to fix.
   */
  const repairCartStore = async (branch, menuRows) => {
    const current = cartRef.current;
    if (!branch?.id || current.length === 0 || repairInFlightRef.current) return null;
    if (menuRows && foreignCartLines(current, menuRows).length === 0) return null;
    repairInFlightRef.current = true;
    try {
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const res = await axios.post(`${API}/cart/remap`, { items: current, target_branch_id: branch.id }, cfg);
      // The customer may have changed the cart while the call was out — don't
      // overwrite that; the next menu fetch asks again.
      if (cartRef.current !== current) return null;
      const items = res.data?.items || [];
      // Every line was already this store's (a stale batch response) — nothing to say.
      const unchanged = items.length === current.length
        && items.every((it, i) => it.menu_item_id === current[i].menu_item_id);
      if (unchanged) return null;
      await updateCart(items);
      return {
        branch: res.data?.branch || branch,
        moved: res.data?.moved || [],
        dropped: res.data?.dropped || [],
        offersRemoved: res.data?.offers_removed || [],
      };
    } finally {
      repairInFlightRef.current = false;
    }
  };

  // The cart subtotal on the coupon basis — the same figure create_order calls
  // `subtotal_for_coupon`: every line at its full size-aware price, less the
  // daily-offer savings (Business Rule #3), less `extraSavings` (the Tiffin of
  // the Day upsell total, Business Rule #58, which the page computes from the
  // day's plan). Customization prices are live-derived from the spec + current
  // menu options (utils/pricing.js) so a stale cart value can't poison it.
  // A sized daily-offer line used to add the size delta on top of the offer
  // price; the server never did — it is now offer price + extras, like the server.
  const getCartTotal = (menuItems, extraSavings = 0) => {
    const total = cart.reduce((sum, item) => {
      const menuItem = menuItems.find(m => m.id === item.menu_item_id);
      const customPrice = getCustomizationPrice(item, menuItem);
      if (item.is_free_coffee || item.is_sub_free) {
        return sum + customPrice * item.quantity;
      }
      if (!menuItem) {
        const basePrice = item.offer_price != null ? item.offer_price : 0;
        return sum + (basePrice + customPrice) * item.quantity;
      }
      return sum + (menuItem.price + customPrice) * item.quantity - getLineDailyOfferSavings(item, menuItem);
    }, 0);
    return Math.max(0, total - (extraSavings || 0));
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const updateItemCustomization = (menuItemId, customizations, customizationPrice) => {
    const newCart = cart.map(item =>
      item.menu_item_id === menuItemId
        ? { ...item, customization: customizations, customization_price: customizationPrice }
        : item
    );
    updateCart(newCart);
  };

  // Business Rule #37 — set one taste dimension on a cart line (by index). The
  // per-line `taste_overrides` travels with the cart into the order payload and
  // drives THAT order's hidden barista note; only dimensions the customer
  // actually taps are written (an untouched line carries no override and falls
  // back to the saved profile pref server-side). Index-keyed because the Cart
  // page identifies a line by its array position (`_cartIndex`).
  const setItemTaste = (index, dimension, value) => {
    if (index < 0 || index >= cart.length) return;
    const newCart = cart.map((item, i) =>
      i === index
        ? { ...item, taste_overrides: { ...(item.taste_overrides || {}), [dimension]: value } }
        : item
    );
    updateCart(newCart);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        cartReady,
        addToCart,
        addItemsToCart,
        addFreeCoffee,
        hasFreeCoffeeInCart,
        removeFromCart,
        removeByIndex,
        updateQuantity,
        updateQuantityAt,
        clearCart,
        switchBranch,
        repairCartStore,
        getCartTotal,
        getCartCount,
        updateItemCustomization,
        setItemTaste,
        rememberTaste,
        setRememberTaste,
        fetchCart,
        cartDrawerOpen,
        setCartDrawerOpen,
        cartDrawerNonce,
        appliedDiscount,
        setAppliedDiscount,
        activeBogoOffer,
        setActiveBogoOffer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
